import { Footer as EndesignFooter } from '@enuygun/frontend-components'
import { Wingie, WingieAr } from '@enuygun/icons'
import { Trans, useTranslation } from 'next-i18next'
import { useRef, useMemo } from 'react'

import EmailManagementDialogWingie from 'components/EmailManagementDialogWingie'
import { useAppState } from 'context/AppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import getPaymentLogos from 'utils/getPaymentLogos'
import { buildInternationalSitesSeeAllUrl } from 'utils/wingieApiLinks'
import { FooterWrapper } from './styled'
import { defaultFooterLinksWithFlags } from './trFooterData'
import { getStaticFooterData } from './data'

interface IFooterSection extends IFooterNavItem {
  items: IFooterNavItem[]
  dataTestId?: string
}
interface IFooterNavItem {
  title?: string
  href?: string
  index?: string | number
  rel?: string
  target?: string
  children?: React.ReactNode
}

const getFooterLogo = (language: string | undefined, isWingie: boolean) => {
  if (language === 'ar') {
    return { svg: <WingieAr width={140} height={55} /> }
  }
  if (isWingie) {
    return { svg: <Wingie style={{ marginTop: '10px' }} width={144} height={24} /> }
  }
  return undefined
}

const getIataContent = ({ isWingie, isMobile, market, t }: { isWingie: boolean; isMobile: boolean; market: string | undefined; t: (key: string) => string }) => {
  if (!isWingie && isMobile) {
    return [
      <div key="0" style={{ textAlign: 'center' }}>
        <Trans i18nKey="Footer.iataMobile"></Trans>
      </div>,
    ]
  }

  if (isWingie && market === 'sa') {
    if (isMobile) {
      return [
        <div key="0" style={{ textAlign: 'center' }}>
          <Trans i18nKey="Footer.iataSaMobile"></Trans>
        </div>,
      ]
    }
    return [<div key="0" dangerouslySetInnerHTML={{ __html: t('Footer.iataSa') }}></div>]
  }

  return [t('Footer.iata')]
}

const Footer = ({ data, footerTrans }) => {
  const { market, language, endpoint } = useAppState()
  let isWingie = market !== 'tr'
  const internationalSitesSeeAllHref = useMemo(() => {
    if (!isWingie) return 'https://www.enuygun.com/uluslararasi-siteler/'
    return buildInternationalSitesSeeAllUrl(endpoint) || undefined
  }, [isWingie, endpoint])

  const isMobile = useIsMobile()
  const footerRef = useRef<HTMLDivElement>(null)

  // Footer verilerini belirle: API'den gelen veri, yoksa fallback olarak statik veri
  const footerData = useMemo(() => {
    // TR market için her zaman statik footer
    if (!isWingie) {
      // console.log(`\n📍 [FOOTER COMPONENT] TR market - Statik veri kullanılıyor`)
      // console.log(`   └─ defaultFooterLinksWithFlags (${defaultFooterLinksWithFlags.length} section)\n`)

      return {
        Links: defaultFooterLinksWithFlags,
        moreLinks: undefined,
      }
    }

    // Links: en az 2 section (örn. topDestinations gelmezse diğer 2 yeterli); moreLinks: en az 1 section
    const MIN_LINKS_SECTIONS = 2
    const MIN_MORE_LINKS_SECTIONS = 1
    const hasEnoughApiData = (data?.Links?.length ?? 0) >= MIN_LINKS_SECTIONS && (data?.moreLinks?.length ?? 0) >= MIN_MORE_LINKS_SECTIONS

    if (hasEnoughApiData) {
      return {
        Links: transformChildrenToComponents(data?.Links) || [],
        moreLinks: transformChildrenToComponents(data?.moreLinks),
      }
    }

    // API verisi eksik veya yetersizse statik veriye geç
    // console.log(`\n📍 [FOOTER COMPONENT] ${market}-${language} - Component seviyesinde fallback`)
    // console.log(`   └─ API verisi boş, statik veriler kontrol ediliyor...\n`)

    const staticData = getStaticFooterData(market || '', language || '')

    if (staticData) {
      // console.log(`✅ [FOOTER COMPONENT] Statik veri bulundu: ${market}-${language}`)
      // console.log(`   ├─ Links: ${staticData.Links?.length || 0} section`)
      // console.log(`   └─ MoreLinks: ${staticData.moreLinks?.length || 0} section\n`)

      return {
        Links: transformChildrenToComponents(staticData.Links) || [],
        moreLinks: transformChildrenToComponents(staticData.moreLinks),
      }
    }

    // Hiçbir veri yoksa boş dön
    // console.warn(`\n❌ [FOOTER COMPONENT] Hiçbir footer verisi bulunamadı: ${market}-${language}\n`)
    return {
      Links: [],
      moreLinks: undefined,
    }
  }, [isWingie, data, market, language])

  const footerLinks = footerData.Links
  const moreItems: IFooterSection[] | undefined = footerData.moreLinks
  const logo = getFooterLogo(language, isWingie)
  const currentYear = new Date().getFullYear()
  const { t } = useTranslation()
  const iata = useMemo(() => getIataContent({ isWingie, isMobile: !!isMobile, market, t }), [isWingie, isMobile, market, t])

  const copyrightText = t('Footer.copyright', { currentYear })
  // Payment logos - sadece belirli market-language kombinasyonlarında gösterilir
  // Etbis logosunun boşluğunda olacaklar (etbis false olduğunda)
  const paymentLogos = useMemo(() => getPaymentLogos(market, language), [market, language])
  const paymentLogosText = useMemo(
    () =>
      paymentLogos ? (
        <span data-testid="payment-logos-text" className="payment-logos-text">
          {t('Footer.paymentLogosText')}
        </span>
      ) : undefined,
    [paymentLogos, t]
  )

  return (
    <FooterWrapper ref={footerRef} isWingie={isWingie}>
      <EndesignFooter
        logo={logo}
        dataTestId={`${isWingie ? 'wingie' : 'enuygun'}-homepage`}
        navigationItems={footerLinks}
        moreItems={moreItems}
        internationalSites
        {...(internationalSitesSeeAllHref && { internationalSitesSeeAllHref })}
        {...(isWingie && { etbis: false })}
        {...(isWingie && { trustStamp: false })}
        iata={iata}
        copyRight={copyrightText}
        KVKK={false}
        {...(paymentLogos &&
          paymentLogosText && {
            paymentLogos,
            paymentLogosText,
          })}
        {...(footerTrans && { footerTrans })}
      />
    </FooterWrapper>
  )
}

export default Footer

const componentMap = { EmailManagementDialogWingie: <EmailManagementDialogWingie /> }

function transformChildrenToComponents(links: IFooterSection[]) {
  return links?.map((link) => ({
    ...link,
    //@ts-ignore
    items: link?.items?.map((item) => (item?.children && componentMap[item?.children] ? { children: componentMap[item?.children] } : { title: item.title, ...item })),
  }))
}
